/**
 * Pre-built Meal Templates
 * Organized by goal category: weight loss, muscle gain, maintenance
 */

export const TEMPLATE_CATEGORIES = {
  weight_loss: {
    id: 'weight_loss',
    label: 'Weight Loss',
    description: 'Low-calorie, high-protein meals to support fat loss',
    color: '#e74c3c',
  },
  muscle_gain: {
    id: 'muscle_gain',
    label: 'Muscle Gain',
    description: 'High-protein, calorie-dense meals for building muscle',
    color: '#3498db',
  },
  maintenance: {
    id: 'maintenance',
    label: 'Maintenance',
    description: 'Balanced meals for maintaining current weight',
    color: '#2ecc71',
  },
  custom: {
    id: 'custom',
    label: 'Custom',
    description: 'Your personal meal templates',
    color: '#9b59b6',
  },
};

export const MEAL_TYPES = {
  breakfast: { id: 'breakfast', label: 'Breakfast', icon: 'Coffee' },
  lunch: { id: 'lunch', label: 'Lunch', icon: 'Sun' },
  dinner: { id: 'dinner', label: 'Dinner', icon: 'Moon' },
  snack: { id: 'snack', label: 'Snack', icon: 'Cookie' },
};

/**
 * Pre-built meal templates with common combinations
 */
export const prebuiltTemplates = [
  // WEIGHT LOSS TEMPLATES
  {
    id: 'wl_light_start',
    name: 'Light Start Day',
    description: 'A low-calorie day plan perfect for jump-starting weight loss (~1,200 cal)',
    category: 'weight_loss',
    isPrebuilt: true,
    tags: ['low-calorie', 'high-protein', 'beginner'],
    meals: [
      {
        id: 1,
        mealType: 'breakfast',
        name: 'Protein Breakfast',
        foods: [
          {
            id: 101,
            name: 'Egg whites (4)',
            quantity: 1,
            unit: 'serving',
            calories: 68,
            protein: 14,
            carbs: 1,
            fat: 0,
          },
          {
            id: 102,
            name: 'Whole grain toast',
            quantity: 1,
            unit: 'slice',
            calories: 80,
            protein: 4,
            carbs: 15,
            fat: 1,
          },
          {
            id: 103,
            name: 'Avocado',
            quantity: 0.25,
            unit: 'medium',
            calories: 60,
            protein: 1,
            carbs: 3,
            fat: 5,
          },
        ],
      },
      {
        id: 2,
        mealType: 'lunch',
        name: 'Lean Lunch',
        foods: [
          {
            id: 201,
            name: 'Grilled chicken breast',
            quantity: 4,
            unit: 'oz',
            calories: 140,
            protein: 26,
            carbs: 0,
            fat: 3,
          },
          {
            id: 202,
            name: 'Mixed greens salad',
            quantity: 2,
            unit: 'cups',
            calories: 20,
            protein: 2,
            carbs: 4,
            fat: 0,
          },
          {
            id: 203,
            name: 'Olive oil dressing',
            quantity: 1,
            unit: 'tbsp',
            calories: 120,
            protein: 0,
            carbs: 0,
            fat: 14,
          },
          {
            id: 204,
            name: 'Cherry tomatoes',
            quantity: 0.5,
            unit: 'cup',
            calories: 15,
            protein: 1,
            carbs: 3,
            fat: 0,
          },
        ],
      },
      {
        id: 3,
        mealType: 'snack',
        name: 'Afternoon Snack',
        foods: [
          {
            id: 301,
            name: 'Greek yogurt (non-fat)',
            quantity: 1,
            unit: 'cup',
            calories: 100,
            protein: 17,
            carbs: 6,
            fat: 0,
          },
          {
            id: 302,
            name: 'Blueberries',
            quantity: 0.5,
            unit: 'cup',
            calories: 40,
            protein: 1,
            carbs: 10,
            fat: 0,
          },
        ],
      },
      {
        id: 4,
        mealType: 'dinner',
        name: 'Light Dinner',
        foods: [
          {
            id: 401,
            name: 'Baked salmon fillet',
            quantity: 4,
            unit: 'oz',
            calories: 180,
            protein: 23,
            carbs: 0,
            fat: 10,
          },
          {
            id: 402,
            name: 'Steamed broccoli',
            quantity: 1,
            unit: 'cup',
            calories: 55,
            protein: 4,
            carbs: 11,
            fat: 0,
          },
          {
            id: 403,
            name: 'Quinoa',
            quantity: 0.5,
            unit: 'cup',
            calories: 110,
            protein: 4,
            carbs: 20,
            fat: 2,
          },
        ],
      },
    ],
    totalNutrition: { calories: 988, protein: 97, carbs: 73, fat: 35 },
  },
  {
    id: 'wl_balanced',
    name: 'Balanced Deficit',
    description: 'Sustainable weight loss plan with satisfying portions (~1,500 cal)',
    category: 'weight_loss',
    isPrebuilt: true,
    tags: ['balanced', 'sustainable', 'moderate'],
    meals: [
      {
        id: 1,
        mealType: 'breakfast',
        name: 'Oatmeal Power Bowl',
        foods: [
          {
            id: 101,
            name: 'Steel cut oatmeal',
            quantity: 0.5,
            unit: 'cup',
            calories: 150,
            protein: 5,
            carbs: 27,
            fat: 3,
          },
          {
            id: 102,
            name: 'Protein powder',
            quantity: 0.5,
            unit: 'scoop',
            calories: 60,
            protein: 12,
            carbs: 2,
            fat: 1,
          },
          {
            id: 103,
            name: 'Banana',
            quantity: 0.5,
            unit: 'medium',
            calories: 53,
            protein: 1,
            carbs: 13,
            fat: 0,
          },
          {
            id: 104,
            name: 'Almond butter',
            quantity: 1,
            unit: 'tbsp',
            calories: 98,
            protein: 3,
            carbs: 3,
            fat: 9,
          },
        ],
      },
      {
        id: 2,
        mealType: 'lunch',
        name: 'Turkey Wrap',
        foods: [
          {
            id: 201,
            name: 'Whole wheat tortilla',
            quantity: 1,
            unit: 'large',
            calories: 140,
            protein: 4,
            carbs: 24,
            fat: 3,
          },
          {
            id: 202,
            name: 'Sliced turkey breast',
            quantity: 4,
            unit: 'oz',
            calories: 120,
            protein: 24,
            carbs: 0,
            fat: 2,
          },
          {
            id: 203,
            name: 'Hummus',
            quantity: 2,
            unit: 'tbsp',
            calories: 70,
            protein: 2,
            carbs: 6,
            fat: 4,
          },
          {
            id: 204,
            name: 'Spinach',
            quantity: 1,
            unit: 'cup',
            calories: 7,
            protein: 1,
            carbs: 1,
            fat: 0,
          },
          {
            id: 205,
            name: 'Bell peppers',
            quantity: 0.5,
            unit: 'cup',
            calories: 15,
            protein: 0,
            carbs: 4,
            fat: 0,
          },
        ],
      },
      {
        id: 3,
        mealType: 'snack',
        name: 'Apple & Cheese',
        foods: [
          {
            id: 301,
            name: 'Apple',
            quantity: 1,
            unit: 'medium',
            calories: 95,
            protein: 0,
            carbs: 25,
            fat: 0,
          },
          {
            id: 302,
            name: 'String cheese',
            quantity: 1,
            unit: 'piece',
            calories: 80,
            protein: 7,
            carbs: 1,
            fat: 6,
          },
        ],
      },
      {
        id: 4,
        mealType: 'dinner',
        name: 'Lean Stir Fry',
        foods: [
          {
            id: 401,
            name: 'Chicken breast',
            quantity: 5,
            unit: 'oz',
            calories: 165,
            protein: 31,
            carbs: 0,
            fat: 4,
          },
          {
            id: 402,
            name: 'Mixed vegetables',
            quantity: 2,
            unit: 'cups',
            calories: 80,
            protein: 4,
            carbs: 16,
            fat: 0,
          },
          {
            id: 403,
            name: 'Brown rice',
            quantity: 0.5,
            unit: 'cup',
            calories: 108,
            protein: 2,
            carbs: 22,
            fat: 1,
          },
          {
            id: 404,
            name: 'Low-sodium soy sauce',
            quantity: 2,
            unit: 'tbsp',
            calories: 20,
            protein: 2,
            carbs: 2,
            fat: 0,
          },
        ],
      },
    ],
    totalNutrition: { calories: 1261, protein: 98, carbs: 146, fat: 33 },
  },
  {
    id: 'wl_high_protein',
    name: 'High Protein Cut',
    description: 'Maximum protein for preserving muscle while losing fat (~1,400 cal)',
    category: 'weight_loss',
    isPrebuilt: true,
    tags: ['high-protein', 'muscle-sparing', 'fitness'],
    meals: [
      {
        id: 1,
        mealType: 'breakfast',
        name: 'Protein Scramble',
        foods: [
          {
            id: 101,
            name: 'Whole eggs',
            quantity: 2,
            unit: 'large',
            calories: 140,
            protein: 12,
            carbs: 1,
            fat: 10,
          },
          {
            id: 102,
            name: 'Egg whites',
            quantity: 4,
            unit: 'large',
            calories: 68,
            protein: 14,
            carbs: 1,
            fat: 0,
          },
          {
            id: 103,
            name: 'Turkey sausage',
            quantity: 2,
            unit: 'links',
            calories: 100,
            protein: 10,
            carbs: 1,
            fat: 6,
          },
        ],
      },
      {
        id: 2,
        mealType: 'lunch',
        name: 'Tuna Power Salad',
        foods: [
          {
            id: 201,
            name: 'Tuna (in water)',
            quantity: 5,
            unit: 'oz',
            calories: 130,
            protein: 29,
            carbs: 0,
            fat: 1,
          },
          {
            id: 202,
            name: 'Mixed greens',
            quantity: 3,
            unit: 'cups',
            calories: 30,
            protein: 3,
            carbs: 6,
            fat: 0,
          },
          {
            id: 203,
            name: 'Chickpeas',
            quantity: 0.25,
            unit: 'cup',
            calories: 70,
            protein: 4,
            carbs: 12,
            fat: 1,
          },
          {
            id: 204,
            name: 'Balsamic vinaigrette',
            quantity: 2,
            unit: 'tbsp',
            calories: 45,
            protein: 0,
            carbs: 3,
            fat: 4,
          },
        ],
      },
      {
        id: 3,
        mealType: 'snack',
        name: 'Cottage Cheese Bowl',
        foods: [
          {
            id: 301,
            name: 'Low-fat cottage cheese',
            quantity: 1,
            unit: 'cup',
            calories: 163,
            protein: 28,
            carbs: 6,
            fat: 2,
          },
          {
            id: 302,
            name: 'Strawberries',
            quantity: 0.5,
            unit: 'cup',
            calories: 25,
            protein: 1,
            carbs: 6,
            fat: 0,
          },
        ],
      },
      {
        id: 4,
        mealType: 'dinner',
        name: 'Grilled Protein Plate',
        foods: [
          {
            id: 401,
            name: 'Lean beef sirloin',
            quantity: 5,
            unit: 'oz',
            calories: 210,
            protein: 35,
            carbs: 0,
            fat: 7,
          },
          {
            id: 402,
            name: 'Asparagus',
            quantity: 1,
            unit: 'cup',
            calories: 27,
            protein: 3,
            carbs: 5,
            fat: 0,
          },
          {
            id: 403,
            name: 'Sweet potato',
            quantity: 0.5,
            unit: 'medium',
            calories: 60,
            protein: 1,
            carbs: 14,
            fat: 0,
          },
        ],
      },
    ],
    totalNutrition: { calories: 1068, protein: 140, carbs: 55, fat: 31 },
  },

  // MUSCLE GAIN TEMPLATES
  {
    id: 'mg_bulk_basic',
    name: 'Basic Bulk',
    description: 'Clean calorie surplus for steady muscle gain (~2,500 cal)',
    category: 'muscle_gain',
    isPrebuilt: true,
    tags: ['bulking', 'high-calorie', 'clean'],
    meals: [
      {
        id: 1,
        mealType: 'breakfast',
        name: 'Power Breakfast',
        foods: [
          {
            id: 101,
            name: 'Whole eggs',
            quantity: 3,
            unit: 'large',
            calories: 210,
            protein: 18,
            carbs: 1,
            fat: 15,
          },
          {
            id: 102,
            name: 'Oatmeal',
            quantity: 1,
            unit: 'cup',
            calories: 300,
            protein: 10,
            carbs: 54,
            fat: 6,
          },
          {
            id: 103,
            name: 'Whole milk',
            quantity: 1,
            unit: 'cup',
            calories: 150,
            protein: 8,
            carbs: 12,
            fat: 8,
          },
          {
            id: 104,
            name: 'Banana',
            quantity: 1,
            unit: 'large',
            calories: 121,
            protein: 1,
            carbs: 31,
            fat: 0,
          },
        ],
      },
      {
        id: 2,
        mealType: 'lunch',
        name: 'Chicken & Rice',
        foods: [
          {
            id: 201,
            name: 'Chicken thighs',
            quantity: 6,
            unit: 'oz',
            calories: 280,
            protein: 36,
            carbs: 0,
            fat: 14,
          },
          {
            id: 202,
            name: 'White rice',
            quantity: 1.5,
            unit: 'cups',
            calories: 320,
            protein: 6,
            carbs: 70,
            fat: 1,
          },
          {
            id: 203,
            name: 'Black beans',
            quantity: 0.5,
            unit: 'cup',
            calories: 115,
            protein: 8,
            carbs: 20,
            fat: 0,
          },
          {
            id: 204,
            name: 'Olive oil',
            quantity: 1,
            unit: 'tbsp',
            calories: 120,
            protein: 0,
            carbs: 0,
            fat: 14,
          },
        ],
      },
      {
        id: 3,
        mealType: 'snack',
        name: 'Muscle Shake',
        foods: [
          {
            id: 301,
            name: 'Protein powder',
            quantity: 2,
            unit: 'scoops',
            calories: 240,
            protein: 48,
            carbs: 8,
            fat: 4,
          },
          {
            id: 302,
            name: 'Peanut butter',
            quantity: 2,
            unit: 'tbsp',
            calories: 190,
            protein: 8,
            carbs: 6,
            fat: 16,
          },
          {
            id: 303,
            name: 'Whole milk',
            quantity: 1,
            unit: 'cup',
            calories: 150,
            protein: 8,
            carbs: 12,
            fat: 8,
          },
        ],
      },
      {
        id: 4,
        mealType: 'dinner',
        name: 'Steak Dinner',
        foods: [
          {
            id: 401,
            name: 'Ribeye steak',
            quantity: 8,
            unit: 'oz',
            calories: 544,
            protein: 46,
            carbs: 0,
            fat: 40,
          },
          {
            id: 402,
            name: 'Baked potato',
            quantity: 1,
            unit: 'large',
            calories: 278,
            protein: 7,
            carbs: 63,
            fat: 0,
          },
          {
            id: 403,
            name: 'Butter',
            quantity: 1,
            unit: 'tbsp',
            calories: 102,
            protein: 0,
            carbs: 0,
            fat: 12,
          },
          {
            id: 404,
            name: 'Broccoli',
            quantity: 1,
            unit: 'cup',
            calories: 55,
            protein: 4,
            carbs: 11,
            fat: 0,
          },
        ],
      },
    ],
    totalNutrition: { calories: 2875, protein: 208, carbs: 288, fat: 138 },
  },
  {
    id: 'mg_lean_gains',
    name: 'Lean Gains',
    description: 'High protein, moderate surplus for lean muscle growth (~2,200 cal)',
    category: 'muscle_gain',
    isPrebuilt: true,
    tags: ['lean', 'high-protein', 'moderate'],
    meals: [
      {
        id: 1,
        mealType: 'breakfast',
        name: 'Eggs & Toast',
        foods: [
          {
            id: 101,
            name: 'Whole eggs',
            quantity: 4,
            unit: 'large',
            calories: 280,
            protein: 24,
            carbs: 2,
            fat: 20,
          },
          {
            id: 102,
            name: 'Whole wheat bread',
            quantity: 2,
            unit: 'slices',
            calories: 160,
            protein: 8,
            carbs: 30,
            fat: 2,
          },
          {
            id: 103,
            name: 'Avocado',
            quantity: 0.5,
            unit: 'medium',
            calories: 120,
            protein: 2,
            carbs: 6,
            fat: 10,
          },
        ],
      },
      {
        id: 2,
        mealType: 'lunch',
        name: 'Salmon Bowl',
        foods: [
          {
            id: 201,
            name: 'Grilled salmon',
            quantity: 6,
            unit: 'oz',
            calories: 270,
            protein: 34,
            carbs: 0,
            fat: 15,
          },
          {
            id: 202,
            name: 'Brown rice',
            quantity: 1,
            unit: 'cup',
            calories: 216,
            protein: 5,
            carbs: 45,
            fat: 2,
          },
          {
            id: 203,
            name: 'Edamame',
            quantity: 0.5,
            unit: 'cup',
            calories: 95,
            protein: 8,
            carbs: 8,
            fat: 4,
          },
          {
            id: 204,
            name: 'Mixed vegetables',
            quantity: 1,
            unit: 'cup',
            calories: 40,
            protein: 2,
            carbs: 8,
            fat: 0,
          },
        ],
      },
      {
        id: 3,
        mealType: 'snack',
        name: 'Greek Yogurt Mix',
        foods: [
          {
            id: 301,
            name: 'Greek yogurt (whole)',
            quantity: 1,
            unit: 'cup',
            calories: 190,
            protein: 18,
            carbs: 8,
            fat: 10,
          },
          {
            id: 302,
            name: 'Granola',
            quantity: 0.25,
            unit: 'cup',
            calories: 120,
            protein: 3,
            carbs: 20,
            fat: 4,
          },
          {
            id: 303,
            name: 'Honey',
            quantity: 1,
            unit: 'tbsp',
            calories: 64,
            protein: 0,
            carbs: 17,
            fat: 0,
          },
        ],
      },
      {
        id: 4,
        mealType: 'dinner',
        name: 'Turkey Pasta',
        foods: [
          {
            id: 401,
            name: 'Ground turkey (93% lean)',
            quantity: 6,
            unit: 'oz',
            calories: 250,
            protein: 36,
            carbs: 0,
            fat: 12,
          },
          {
            id: 402,
            name: 'Whole wheat pasta',
            quantity: 2,
            unit: 'oz',
            calories: 180,
            protein: 8,
            carbs: 36,
            fat: 2,
          },
          {
            id: 403,
            name: 'Marinara sauce',
            quantity: 0.5,
            unit: 'cup',
            calories: 50,
            protein: 2,
            carbs: 10,
            fat: 1,
          },
          {
            id: 404,
            name: 'Parmesan cheese',
            quantity: 2,
            unit: 'tbsp',
            calories: 44,
            protein: 4,
            carbs: 0,
            fat: 3,
          },
        ],
      },
    ],
    totalNutrition: { calories: 2079, protein: 154, carbs: 190, fat: 85 },
  },
  {
    id: 'mg_athlete',
    name: 'Athlete Fuel',
    description: 'High-performance nutrition for intense training (~2,800 cal)',
    category: 'muscle_gain',
    isPrebuilt: true,
    tags: ['athlete', 'performance', 'high-carb'],
    meals: [
      {
        id: 1,
        mealType: 'breakfast',
        name: 'Champion Breakfast',
        foods: [
          {
            id: 101,
            name: 'Egg whites',
            quantity: 6,
            unit: 'large',
            calories: 102,
            protein: 21,
            carbs: 1,
            fat: 0,
          },
          {
            id: 102,
            name: 'Whole eggs',
            quantity: 2,
            unit: 'large',
            calories: 140,
            protein: 12,
            carbs: 1,
            fat: 10,
          },
          {
            id: 103,
            name: 'Whole grain pancakes',
            quantity: 3,
            unit: 'medium',
            calories: 280,
            protein: 8,
            carbs: 45,
            fat: 8,
          },
          {
            id: 104,
            name: 'Maple syrup',
            quantity: 2,
            unit: 'tbsp',
            calories: 104,
            protein: 0,
            carbs: 27,
            fat: 0,
          },
          {
            id: 105,
            name: 'Orange juice',
            quantity: 1,
            unit: 'cup',
            calories: 110,
            protein: 2,
            carbs: 26,
            fat: 0,
          },
        ],
      },
      {
        id: 2,
        mealType: 'lunch',
        name: 'Power Bowl',
        foods: [
          {
            id: 201,
            name: 'Grilled chicken',
            quantity: 8,
            unit: 'oz',
            calories: 280,
            protein: 52,
            carbs: 0,
            fat: 6,
          },
          {
            id: 202,
            name: 'Jasmine rice',
            quantity: 2,
            unit: 'cups',
            calories: 400,
            protein: 8,
            carbs: 88,
            fat: 0,
          },
          {
            id: 203,
            name: 'Black beans',
            quantity: 0.5,
            unit: 'cup',
            calories: 115,
            protein: 8,
            carbs: 20,
            fat: 0,
          },
          {
            id: 204,
            name: 'Corn',
            quantity: 0.5,
            unit: 'cup',
            calories: 70,
            protein: 2,
            carbs: 15,
            fat: 1,
          },
          {
            id: 205,
            name: 'Salsa',
            quantity: 0.25,
            unit: 'cup',
            calories: 20,
            protein: 1,
            carbs: 4,
            fat: 0,
          },
        ],
      },
      {
        id: 3,
        mealType: 'snack',
        name: 'Pre-Workout Fuel',
        foods: [
          {
            id: 301,
            name: 'Banana',
            quantity: 2,
            unit: 'medium',
            calories: 210,
            protein: 2,
            carbs: 54,
            fat: 0,
          },
          {
            id: 302,
            name: 'Rice cakes',
            quantity: 2,
            unit: 'cakes',
            calories: 70,
            protein: 2,
            carbs: 14,
            fat: 0,
          },
          {
            id: 303,
            name: 'Almond butter',
            quantity: 2,
            unit: 'tbsp',
            calories: 196,
            protein: 6,
            carbs: 6,
            fat: 18,
          },
        ],
      },
      {
        id: 4,
        mealType: 'dinner',
        name: 'Recovery Meal',
        foods: [
          {
            id: 401,
            name: 'Lean beef',
            quantity: 8,
            unit: 'oz',
            calories: 360,
            protein: 56,
            carbs: 0,
            fat: 14,
          },
          {
            id: 402,
            name: 'Sweet potato',
            quantity: 2,
            unit: 'medium',
            calories: 230,
            protein: 4,
            carbs: 52,
            fat: 0,
          },
          {
            id: 403,
            name: 'Green beans',
            quantity: 1,
            unit: 'cup',
            calories: 31,
            protein: 2,
            carbs: 7,
            fat: 0,
          },
          {
            id: 404,
            name: 'Olive oil',
            quantity: 1,
            unit: 'tbsp',
            calories: 120,
            protein: 0,
            carbs: 0,
            fat: 14,
          },
        ],
      },
    ],
    totalNutrition: { calories: 2838, protein: 186, carbs: 360, fat: 71 },
  },

  // MAINTENANCE TEMPLATES
  {
    id: 'mt_balanced',
    name: 'Balanced Day',
    description: 'Well-rounded nutrition for maintaining weight (~2,000 cal)',
    category: 'maintenance',
    isPrebuilt: true,
    tags: ['balanced', 'everyday', 'sustainable'],
    meals: [
      {
        id: 1,
        mealType: 'breakfast',
        name: 'Classic Breakfast',
        foods: [
          {
            id: 101,
            name: 'Whole eggs',
            quantity: 2,
            unit: 'large',
            calories: 140,
            protein: 12,
            carbs: 1,
            fat: 10,
          },
          {
            id: 102,
            name: 'Turkey bacon',
            quantity: 3,
            unit: 'slices',
            calories: 105,
            protein: 9,
            carbs: 0,
            fat: 8,
          },
          {
            id: 103,
            name: 'Whole wheat toast',
            quantity: 2,
            unit: 'slices',
            calories: 160,
            protein: 8,
            carbs: 30,
            fat: 2,
          },
          {
            id: 104,
            name: 'Butter',
            quantity: 1,
            unit: 'tbsp',
            calories: 102,
            protein: 0,
            carbs: 0,
            fat: 12,
          },
        ],
      },
      {
        id: 2,
        mealType: 'lunch',
        name: 'Deli Sandwich',
        foods: [
          {
            id: 201,
            name: 'Whole grain bread',
            quantity: 2,
            unit: 'slices',
            calories: 160,
            protein: 8,
            carbs: 30,
            fat: 2,
          },
          {
            id: 202,
            name: 'Roast beef',
            quantity: 4,
            unit: 'oz',
            calories: 140,
            protein: 24,
            carbs: 0,
            fat: 4,
          },
          {
            id: 203,
            name: 'Swiss cheese',
            quantity: 1,
            unit: 'slice',
            calories: 106,
            protein: 8,
            carbs: 2,
            fat: 8,
          },
          {
            id: 204,
            name: 'Lettuce & tomato',
            quantity: 1,
            unit: 'serving',
            calories: 10,
            protein: 0,
            carbs: 2,
            fat: 0,
          },
          {
            id: 205,
            name: 'Mustard',
            quantity: 1,
            unit: 'tbsp',
            calories: 10,
            protein: 1,
            carbs: 1,
            fat: 0,
          },
          {
            id: 206,
            name: 'Apple',
            quantity: 1,
            unit: 'medium',
            calories: 95,
            protein: 0,
            carbs: 25,
            fat: 0,
          },
        ],
      },
      {
        id: 3,
        mealType: 'snack',
        name: 'Trail Mix',
        foods: [
          {
            id: 301,
            name: 'Mixed nuts',
            quantity: 0.25,
            unit: 'cup',
            calories: 170,
            protein: 5,
            carbs: 6,
            fat: 15,
          },
          {
            id: 302,
            name: 'Dried cranberries',
            quantity: 2,
            unit: 'tbsp',
            calories: 50,
            protein: 0,
            carbs: 13,
            fat: 0,
          },
        ],
      },
      {
        id: 4,
        mealType: 'dinner',
        name: 'Grilled Chicken Dinner',
        foods: [
          {
            id: 401,
            name: 'Chicken breast',
            quantity: 6,
            unit: 'oz',
            calories: 210,
            protein: 39,
            carbs: 0,
            fat: 5,
          },
          {
            id: 402,
            name: 'Roasted potatoes',
            quantity: 1,
            unit: 'cup',
            calories: 160,
            protein: 4,
            carbs: 36,
            fat: 0,
          },
          {
            id: 403,
            name: 'Steamed vegetables',
            quantity: 1.5,
            unit: 'cups',
            calories: 75,
            protein: 4,
            carbs: 15,
            fat: 0,
          },
          {
            id: 404,
            name: 'Olive oil',
            quantity: 1,
            unit: 'tbsp',
            calories: 120,
            protein: 0,
            carbs: 0,
            fat: 14,
          },
        ],
      },
    ],
    totalNutrition: { calories: 1813, protein: 122, carbs: 161, fat: 80 },
  },
  {
    id: 'mt_mediterranean',
    name: 'Mediterranean Style',
    description: 'Heart-healthy meals inspired by the Mediterranean diet (~1,900 cal)',
    category: 'maintenance',
    isPrebuilt: true,
    tags: ['mediterranean', 'heart-healthy', 'fish'],
    meals: [
      {
        id: 1,
        mealType: 'breakfast',
        name: 'Greek Morning',
        foods: [
          {
            id: 101,
            name: 'Greek yogurt',
            quantity: 1,
            unit: 'cup',
            calories: 150,
            protein: 15,
            carbs: 9,
            fat: 6,
          },
          {
            id: 102,
            name: 'Walnuts',
            quantity: 0.25,
            unit: 'cup',
            calories: 185,
            protein: 4,
            carbs: 4,
            fat: 18,
          },
          {
            id: 103,
            name: 'Honey',
            quantity: 1,
            unit: 'tbsp',
            calories: 64,
            protein: 0,
            carbs: 17,
            fat: 0,
          },
          {
            id: 104,
            name: 'Fresh berries',
            quantity: 0.5,
            unit: 'cup',
            calories: 40,
            protein: 1,
            carbs: 10,
            fat: 0,
          },
        ],
      },
      {
        id: 2,
        mealType: 'lunch',
        name: 'Greek Salad Bowl',
        foods: [
          {
            id: 201,
            name: 'Grilled chicken',
            quantity: 4,
            unit: 'oz',
            calories: 140,
            protein: 26,
            carbs: 0,
            fat: 3,
          },
          {
            id: 202,
            name: 'Mixed greens',
            quantity: 2,
            unit: 'cups',
            calories: 20,
            protein: 2,
            carbs: 4,
            fat: 0,
          },
          {
            id: 203,
            name: 'Feta cheese',
            quantity: 2,
            unit: 'oz',
            calories: 150,
            protein: 8,
            carbs: 2,
            fat: 12,
          },
          {
            id: 204,
            name: 'Cucumber',
            quantity: 0.5,
            unit: 'cup',
            calories: 8,
            protein: 0,
            carbs: 2,
            fat: 0,
          },
          {
            id: 205,
            name: 'Kalamata olives',
            quantity: 6,
            unit: 'olives',
            calories: 60,
            protein: 0,
            carbs: 2,
            fat: 6,
          },
          {
            id: 206,
            name: 'Olive oil & lemon dressing',
            quantity: 2,
            unit: 'tbsp',
            calories: 120,
            protein: 0,
            carbs: 1,
            fat: 14,
          },
          {
            id: 207,
            name: 'Pita bread',
            quantity: 1,
            unit: 'small',
            calories: 80,
            protein: 3,
            carbs: 15,
            fat: 1,
          },
        ],
      },
      {
        id: 3,
        mealType: 'snack',
        name: 'Hummus & Veggies',
        foods: [
          {
            id: 301,
            name: 'Hummus',
            quantity: 0.25,
            unit: 'cup',
            calories: 140,
            protein: 4,
            carbs: 12,
            fat: 8,
          },
          {
            id: 302,
            name: 'Carrot sticks',
            quantity: 1,
            unit: 'cup',
            calories: 50,
            protein: 1,
            carbs: 12,
            fat: 0,
          },
          {
            id: 303,
            name: 'Cucumber slices',
            quantity: 0.5,
            unit: 'cup',
            calories: 8,
            protein: 0,
            carbs: 2,
            fat: 0,
          },
        ],
      },
      {
        id: 4,
        mealType: 'dinner',
        name: 'Baked Fish',
        foods: [
          {
            id: 401,
            name: 'Sea bass',
            quantity: 6,
            unit: 'oz',
            calories: 210,
            protein: 40,
            carbs: 0,
            fat: 4,
          },
          {
            id: 402,
            name: 'Roasted vegetables',
            quantity: 1.5,
            unit: 'cups',
            calories: 100,
            protein: 3,
            carbs: 20,
            fat: 2,
          },
          {
            id: 403,
            name: 'Couscous',
            quantity: 0.75,
            unit: 'cup',
            calories: 175,
            protein: 6,
            carbs: 36,
            fat: 0,
          },
          {
            id: 404,
            name: 'Olive oil',
            quantity: 1,
            unit: 'tbsp',
            calories: 120,
            protein: 0,
            carbs: 0,
            fat: 14,
          },
          {
            id: 405,
            name: 'Lemon',
            quantity: 0.5,
            unit: 'medium',
            calories: 8,
            protein: 0,
            carbs: 2,
            fat: 0,
          },
        ],
      },
    ],
    totalNutrition: { calories: 1828, protein: 113, carbs: 150, fat: 88 },
  },
  {
    id: 'mt_vegetarian',
    name: 'Vegetarian Day',
    description: 'Plant-based nutrition meeting all protein needs (~1,800 cal)',
    category: 'maintenance',
    isPrebuilt: true,
    tags: ['vegetarian', 'plant-based', 'meatless'],
    meals: [
      {
        id: 1,
        mealType: 'breakfast',
        name: 'Veggie Scramble',
        foods: [
          {
            id: 101,
            name: 'Tofu scramble',
            quantity: 1,
            unit: 'cup',
            calories: 180,
            protein: 15,
            carbs: 4,
            fat: 12,
          },
          {
            id: 102,
            name: 'Sautéed spinach',
            quantity: 1,
            unit: 'cup',
            calories: 40,
            protein: 5,
            carbs: 6,
            fat: 0,
          },
          {
            id: 103,
            name: 'Whole wheat toast',
            quantity: 2,
            unit: 'slices',
            calories: 160,
            protein: 8,
            carbs: 30,
            fat: 2,
          },
          {
            id: 104,
            name: 'Avocado',
            quantity: 0.5,
            unit: 'medium',
            calories: 120,
            protein: 2,
            carbs: 6,
            fat: 10,
          },
        ],
      },
      {
        id: 2,
        mealType: 'lunch',
        name: 'Buddha Bowl',
        foods: [
          {
            id: 201,
            name: 'Quinoa',
            quantity: 1,
            unit: 'cup',
            calories: 220,
            protein: 8,
            carbs: 40,
            fat: 4,
          },
          {
            id: 202,
            name: 'Chickpeas',
            quantity: 0.5,
            unit: 'cup',
            calories: 135,
            protein: 7,
            carbs: 23,
            fat: 2,
          },
          {
            id: 203,
            name: 'Roasted sweet potato',
            quantity: 1,
            unit: 'cup',
            calories: 180,
            protein: 4,
            carbs: 41,
            fat: 0,
          },
          {
            id: 204,
            name: 'Kale',
            quantity: 1,
            unit: 'cup',
            calories: 33,
            protein: 3,
            carbs: 6,
            fat: 0,
          },
          {
            id: 205,
            name: 'Tahini dressing',
            quantity: 2,
            unit: 'tbsp',
            calories: 90,
            protein: 3,
            carbs: 3,
            fat: 8,
          },
        ],
      },
      {
        id: 3,
        mealType: 'snack',
        name: 'Protein Snack',
        foods: [
          {
            id: 301,
            name: 'Edamame',
            quantity: 1,
            unit: 'cup',
            calories: 190,
            protein: 17,
            carbs: 15,
            fat: 8,
          },
          {
            id: 302,
            name: 'Almonds',
            quantity: 0.25,
            unit: 'cup',
            calories: 170,
            protein: 6,
            carbs: 6,
            fat: 15,
          },
        ],
      },
      {
        id: 4,
        mealType: 'dinner',
        name: 'Lentil Curry',
        foods: [
          {
            id: 401,
            name: 'Red lentils',
            quantity: 1,
            unit: 'cup',
            calories: 230,
            protein: 18,
            carbs: 40,
            fat: 1,
          },
          {
            id: 402,
            name: 'Coconut milk',
            quantity: 0.25,
            unit: 'cup',
            calories: 115,
            protein: 1,
            carbs: 2,
            fat: 12,
          },
          {
            id: 403,
            name: 'Brown rice',
            quantity: 1,
            unit: 'cup',
            calories: 216,
            protein: 5,
            carbs: 45,
            fat: 2,
          },
          {
            id: 404,
            name: 'Mixed vegetables',
            quantity: 1,
            unit: 'cup',
            calories: 40,
            protein: 2,
            carbs: 8,
            fat: 0,
          },
        ],
      },
    ],
    totalNutrition: { calories: 2119, protein: 104, carbs: 275, fat: 76 },
  },
];

/**
 * Initialize prebuilt templates in database if not already present
 */
export const initializePrebuiltTemplates = async (saveTemplate) => {
  const existingTemplates = await getAllTemplatesLocal();
  const existingIds = new Set(existingTemplates.map((t) => t.id));

  let addedCount = 0;
  for (const template of prebuiltTemplates) {
    if (!existingIds.has(template.id)) {
      await saveTemplate(template);
      addedCount++;
    }
  }

  return addedCount;
};

// Helper to check existing templates (used by init function)
let getAllTemplatesLocal = async () => [];
export const setGetAllTemplates = (fn) => {
  getAllTemplatesLocal = fn;
};

const templatesModule = {
  TEMPLATE_CATEGORIES,
  MEAL_TYPES,
  prebuiltTemplates,
  initializePrebuiltTemplates,
  setGetAllTemplates,
};

export default templatesModule;
